export enum MetricsTypeEnum {
    SEGMETRICS = 'segmetrics',
    DVH = 'dvh',
    GAMMAINDEX = 'gammaindex',
}

export enum APIType {
    WORKSPACE = '',
    CONTOURING = 'contouring',
    DOSE = 'dose',
    REGISTRATION = 'reg',
    TRANSLATION = 'translation',
    DATA = 'data',
    CONFIG = 'config',
    METRICS = 'metrics',
}

export enum PollingStatus {
    HAS_RESULT = 'HAS_RESULT',
    NO_RESULT = 'NO_RESULT',
    POLLING_FAILED = 'POLLING_FAILED',
}

export interface ResultID {
    id: string;
    model_type: string;
    file_ids: string[];
}

export interface PollingResult {
    result_ids_v2: ResultID[];
    error_ids_v2: ResultID[];
}

export type PollData =
    | {
          status: PollingStatus.HAS_RESULT | PollingStatus.NO_RESULT;
          result?: PollingResult;
          error?: undefined;
      }
    | {
          status: PollingStatus.POLLING_FAILED;
          result?: undefined;
          /** The body the backend answered the failed poll with. */
          error: string;
          /** The error status the backend answered the poll with. */
          httpStatus: number;
      };

export interface ModelDetails {
    model_name: string;
    model_label: string;
    model_type: string;
    body_part: string;
    model_tags: string[];
    input_modalities?: string[];
    output_modality?: string;
    deprecated: boolean;
    customization_names: string[];
    visibility: 'default' | 'always_hide' | 'always_show';
    version: string;
    is_latest: boolean;
}

export interface UserModels {
    all_model_names: string[];
    user_model_names: string[];
    online_model_names: string[];
    all_model_details: ModelDetails[];
    user_model_details: ModelDetails[];
}

export interface Labeling {
    manufacturer: string;
    manufacturer_model_name: string;
    manufacture_date: string;
    clinical: boolean;
    description: string;
    system_version: string;
    udi: string;
}

export const EmptyLabeling: Labeling = {
    manufacturer: '',
    manufacturer_model_name: '',
    manufacture_date: '',
    clinical: false,
    description: '',
    system_version: '',
    udi: '',
};

export interface CustomizationMetadata {
    attribute: string;
    value: string;
}

export interface FMAID {
    code_value: string;
    code_meaning: string;
    coding_scheme_designator: string;
    coding_scheme_version: string;
    mapping_resource: string;
    context_group_version: string;
    context_identifier: string;
    context_uid: string;
    mapping_resource_uid: string;
    mapping_resource_name: string;
}

export interface PhysicalProperty {
    physical_property: string;
    value: string;
}

export interface ContouringCustomizationROI {
    included: boolean;
    can_be_deleted: boolean;
    operation: string;
    color: number[];
    interpreted_type: string;
    name: string;
    fma_id: FMAID;
    physical_properties: PhysicalProperty[];
}

export interface DoseCustomizationROI {
    included: boolean;
    mandatory: boolean;
    roi_name: string;
    reg_exp: string;
}

export interface RegistrationCustomizationROI {
    enabled: boolean;
    included: boolean;
    name: string;
    interpreted_type: string;
}

export interface ContouringCustomizationFile {
    metadata: CustomizationMetadata[];
    rois: ContouringCustomizationROI[];
}

export interface DoseTarget {
    method: string;
    unit: string;
    reg_exp: string;
    prescription: number;
}

export interface DoseScaling {
    attribute: boolean;
    method: string;
    volume: number;
    dose: number;
}

export interface DoseCropping {
    attribute: boolean;
    value: number;
}

export interface DoseCustomizationFile {
    metadata: CustomizationMetadata[];
    rois: DoseCustomizationROI[];
    targets: DoseTarget[];
    dose_scaling: DoseScaling;
    pixel_spacing: number[];
    z_dose_cropping: DoseCropping;
    include_rtplan: boolean;
}

export interface RegistrationResult {
    reg: boolean;
    registered_scan: boolean;
    rtdose: boolean;
    rtplan: boolean;
    rtstruct: boolean;
}

export interface RegistrationCustomizationFile {
    metadata: CustomizationMetadata[];
    rois: RegistrationCustomizationROI[];
    expected_result: RegistrationResult;
    pixel_spacing: number[];
    fixed_series_uid: string;
    moving_series_uid: string;
}

export interface TranslationCustomizationFile {
    metadata: CustomizationMetadata[];
    rois: string[];
}

export interface CustomizationItem<FT> {
    customization_name: string;
    description: string;
    files: FT[];
}

export interface UserCustomization<FT> {
    model_name: string;
    model_label: string;
    model_type: string;
    body_part: string;
    description: string;
    model_tags: string[];
    deprecated: boolean;
    triggers: string[];
    customizations: CustomizationItem<FT>[];
}
